import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';

import { AppModal } from '../components/AppModal';
import { PageHead } from '../components/layout';
import { SearchSelect } from '../components/SearchSelect';
import { useActiveCompanyId, useCompanies } from '../lib/queries/companies';
import {
  useCreateScanRoute,
  useDeleteScanRoute,
  useDiscardScanFile,
  useReleaseScanFile,
  useScanQuarantine,
  useScanRoutes,
  useScanStatus,
} from '../lib/queries/scan-intake';

export const Route = createFileRoute('/settings/scan-intake')({
  component: ScanIntakeSettingsPage,
});

export function ScanIntakeSettingsPage() {
  const companyId = useActiveCompanyId();
  const { data: companies = [] } = useCompanies();
  const { data: status } = useScanStatus(companyId);
  const { data: routes = [], isPending: routesPending } = useScanRoutes(companyId);
  const deleteRoute = useDeleteScanRoute(companyId);
  const { data: quarantine = [] } = useScanQuarantine(companyId);
  const release = useReleaseScanFile(companyId);
  const discard = useDiscardScanFile(companyId);
  const [addRoute, setAddRoute] = useState(false);

  const companyName = (id: string) => {
    const c = companies.find((x) => x.id === id);
    return c ? `${c.code} ${c.nameTh}` : id;
  };

  return (
    <div className="page">
      <PageHead crumbs={['ระบบ', 'รับเอกสารจากสแกน']} title="รับเอกสารจากสแกน (Scan Intake)" />

      <div className="card" style={{ padding: 12, marginBottom: 16 }}>
        <span className={`badge ${status?.enabled ? 'positive' : 'negative'}`}>
          {status?.enabled ? 'เปิดใช้งาน' : 'ปิดใช้งาน'}
        </span>{' '}
        <span className="muted">ที่เก็บ: {status?.bucket ?? '—'}</span>{' '}
        <span className="muted">
          โพลล์ล่าสุด:{' '}
          {status?.lastPollAt ? new Date(status.lastPollAt).toLocaleString('th-TH') : '—'}
        </span>
        {status?.lastError && (
          <div className="muted" style={{ color: 'var(--negative)', fontSize: 12 }}>
            ข้อผิดพลาดล่าสุด: {status.lastError}
          </div>
        )}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <h3 style={{ margin: '8px 0' }}>เส้นทาง (prefix → บริษัท)</h3>
        <button className="btn sm primary" onClick={() => setAddRoute(true)}>
          เพิ่มเส้นทาง
        </button>
      </div>
      <div className="tbl-shell" style={{ marginBottom: 24 }}>
        <table className="tbl">
          <thead>
            <tr>
              <th style={{ width: 240 }}>prefix</th>
              <th>บริษัทปลายทาง</th>
              <th style={{ width: 90 }}>สถานะ</th>
              <th style={{ width: 90 }} />
            </tr>
          </thead>
          <tbody>
            {routesPending ? (
              <tr>
                <td colSpan={4} className="muted" style={{ padding: 24, textAlign: 'center' }}>
                  กำลังโหลด…
                </td>
              </tr>
            ) : routes.length === 0 ? (
              <tr>
                <td colSpan={4} className="muted" style={{ padding: 24, textAlign: 'center' }}>
                  ยังไม่มีเส้นทาง
                </td>
              </tr>
            ) : (
              routes.map((r) => (
                <tr key={r.id}>
                  <td className="code">
                    <b>{r.prefix}</b>
                  </td>
                  <td>{companyName(r.companyId)}</td>
                  <td className="muted">{r.active ? 'ใช้งาน' : 'ปิด'}</td>
                  <td>
                    <button
                      className="btn sm ghost"
                      onClick={() => void deleteRoute.mutateAsync(r.id)}
                    >
                      ลบ
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <h3 style={{ margin: '24px 0 8px' }}>กักกัน (quarantine)</h3>
      <div className="tbl-shell">
        <table className="tbl">
          <thead>
            <tr>
              <th>ไฟล์</th>
              <th style={{ width: 160 }}>เหตุผล</th>
              <th style={{ width: 220 }} />
            </tr>
          </thead>
          <tbody>
            {quarantine.length === 0 ? (
              <tr>
                <td colSpan={3} className="muted" style={{ padding: 24, textAlign: 'center' }}>
                  ไม่มีรายการกักกัน
                </td>
              </tr>
            ) : (
              quarantine.map((f) => (
                <tr key={f.id}>
                  <td className="code">{f.fileName}</td>
                  <td className="muted">{f.reason ?? '—'}</td>
                  <td>
                    <button className="btn sm" onClick={() => void release.mutateAsync(f.id)}>
                      ปล่อยกลับเข้าระบบ
                    </button>{' '}
                    <button
                      className="btn sm ghost"
                      style={{ color: 'var(--negative)' }}
                      onClick={() => void discard.mutateAsync(f.id)}
                    >
                      ทิ้ง
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {addRoute && <ScanRouteModal onClose={() => setAddRoute(false)} />}
    </div>
  );
}

function ScanRouteModal({ onClose }: { onClose: () => void }) {
  const companyId = useActiveCompanyId();
  const { data: companies = [] } = useCompanies();
  const create = useCreateScanRoute(companyId);
  const [prefix, setPrefix] = useState('');
  const [targetCompany, setTargetCompany] = useState(companyId ?? '');
  const companyOptions = companies
    .filter((c): c is typeof c & { id: string } => !!c.id)
    .map((c) => ({ value: c.id, label: `${c.code} ${c.nameTh}` }));

  async function save() {
    await create.mutateAsync({ prefix, companyId: targetCompany, active: true });
    onClose();
  }
  return (
    <AppModal
      title="เพิ่มเส้นทาง (prefix → บริษัท)"
      onClose={onClose}
      footer={
        <>
          <button className="btn ghost" onClick={onClose}>
            ยกเลิก
          </button>
          <button
            className="btn primary"
            disabled={!prefix || !targetCompany || create.isPending}
            onClick={() => void save()}
          >
            บันทึก
          </button>
        </>
      }
    >
      <label className="muted" style={{ fontSize: 13 }}>
        prefix (เช่น dcc/inbox/)
      </label>
      <input
        className="input"
        value={prefix}
        onChange={(e) => setPrefix(e.target.value)}
        style={{ width: '100%', marginBottom: 12 }}
      />
      <label className="muted" style={{ fontSize: 13 }}>
        บริษัทปลายทาง
      </label>
      <SearchSelect
        value={targetCompany}
        onChange={setTargetCompany}
        options={companyOptions}
        getLabel={(v) => companyOptions.find((o) => o.value === v)?.label ?? v}
        ariaLabel="บริษัทปลายทาง"
      />
    </AppModal>
  );
}
